import React, { useMemo, useRef } from "react";

/**
 * Usage:
 * <MyWorksCarousel
 *   title="My Works"
 *   subtitle="Witness the beauty of nature through our lens..."
 *   items={[
 *     { id: 1, title: "Dancing Dunes", desc: "Capturing the breathtaking beauty...", image: "/img/1.jpg", tags:["Photography","Art Direction"], href:"#"},
 *     { id: 2, image: "/img/2.jpg", href:"#"},
 *   ]}
 * />
 */

export default function Portfolio({
title = "Unsere Projekte",

subtitle = "Entdecken Sie ausgewählte Web- und CRM-Lösungen, die Unternehmen helfen, Prozesse zu automatisieren, Leads zu gewinnen und nachhaltiges Wachstum zu erreichen.",

  items = [],
}) {
  const scrollerRef = useRef(null);

  const slideStep = useMemo(() => {
    // scroll by ~1 card width
    return 420;
  }, []);

  const scrollByDir = (dir = "right") => {
    const el = scrollerRef.current;
    if (!el) return;

    const amt = dir === "right" ? slideStep : -slideStep;
    el.scrollBy({ left: amt, behavior: "smooth" });
  };

  return (
    <section className="w-full bg-white">
      <div className="mx-auto max-w-6xl px-4 py-10">
        {/* Header */}
        <div className="relative flex flex-col items-center text-center">
          {/* Left arrow */}
          <button
            type="button"
            onClick={() => scrollByDir("left")}
            className="group absolute left-0 top-1/2 -translate-y-1/2 hidden sm:grid h-10 w-10 place-items-center rounded-full border border-black/10 bg-white/80 shadow-sm backdrop-blur transition hover:bg-white"
            aria-label="Scroll left"
          >
            <span className="text-xl leading-none text-black/70 group-hover:text-black">
              ←
            </span>
          </button>

          <h2 className="h2-mb h2-md h2-lg h2-2xl">
            {title}
          </h2>

          <p className="mt-3 max-w-2xl text-sm sm:text-[15px] leading-relaxed text-black/55">
            {subtitle}
          </p>

          {/* Right arrow */}
          <button
            type="button"
            onClick={() => scrollByDir("right")}
            className="group absolute right-0 top-1/2 -translate-y-1/2 hidden sm:grid h-10 w-10 place-items-center rounded-full border border-black/10 bg-white/80 shadow-sm backdrop-blur transition hover:bg-white"
            aria-label="Scroll right"
          >
            <span className="text-xl leading-none text-black/70 group-hover:text-black">
              →
            </span>
          </button>
        </div>

        {/* Slider */}
        <div className="mt-10">
          <div
            ref={scrollerRef}
            className="
              flex gap-6 overflow-x-auto pb-4
              scroll-smooth
              snap-x snap-mandatory
              [-ms-overflow-style:none] [scrollbar-width:none]
            "
            style={{
              WebkitOverflowScrolling: "touch",
            }}
          >
            {/* hide scrollbar (webkit) */}
            <style>{`
              .hide-scrollbar::-webkit-scrollbar { display: none; }
            `}</style>

            {items.map((it, idx) => (
              <Card key={it.id ?? idx} item={it} />
            ))}
          </div>

          {/* Mobile arrows */}
          <div className="mt-4 flex items-center justify-center gap-3 sm:hidden">
            <button
              type="button"
              onClick={() => scrollByDir("left")}
              className="h-10 w-10 rounded-full border border-black/10 bg-white shadow-sm"
              aria-label="Scroll left"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => scrollByDir("right")}
              className="h-10 w-10 rounded-full border border-black/10 bg-white shadow-sm"
              aria-label="Scroll right"
            >
              →
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}

function Card({ item }) {
  const {
    image,
    title,
    desc,
    tags = [],
    href,
    overlay = true,          // if false => plain image card
    revealOnHover = true,    // if false => overlay always visible
  } = item;

  const showOverlay = overlay && (title || desc || (tags?.length ?? 0) > 0);

  return (
    <a
      href={href || "#"}
      className="
        group relative
        min-w-[280px] sm:min-w-[340px] md:min-w-[360px]
        h-[380px] sm:h-[420px]
        snap-center
        rounded-[28px]
        overflow-hidden
        bg-[#F4F6F8]
        ring-1 ring-black/5
        shadow-[0_20px_60px_-30px_rgba(25,68,99,0.55)]
        transition
        hover:-translate-y-1
      "
    >
      {/* Image */}
      <img
        src={image}
        alt={title || "portfolio"}
        className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]"
        draggable="false"
      />

      {/* Overlay (exact layout like your screenshot) */}
      {showOverlay && (
        <div
          className={[
            "absolute inset-0",
            "p-6",
            "flex flex-col justify-between",
            // reveal behavior:
            revealOnHover
              ? "opacity-0 translate-y-3 transition-all duration-500 ease-out group-hover:opacity-100 group-hover:translate-y-0"
              : "opacity-100 translate-y-0",
          ].join(" ")}
        >
          {/* Dark readability layer */}
          <div
            className="absolute inset-0"
            style={{
              // darker at top + bottom, lighter in middle (like the screenshot)
           background:
"linear-gradient(180deg, rgba(25,68,99,0.65) 0%, rgba(25,68,99,0.45) 45%, rgba(25,68,99,0.80) 100%)"

            }}
          />

          {/* Content sits above overlay */}
          <div className="relative">
            <h3 className="text-[18px] font-semibold text-white">{title}</h3>
            {desc && (
              <p className="mt-2 text-[14px] leading-relaxed text-white/95 ">
                {desc}
              </p>
            )}
          </div>

          {/* Bottom row */}
          <div className="relative flex items-end justify-between gap-4">
            {/* Tags bottom-left */}
            <div className="flex flex-wrap gap-2">
              {tags.slice(0, 3).map((t, i) => (
                <span
                  key={`${t}-${i}`}
                  className="
                    rounded-full px-3 py-1 text-[11px]
                    border border-white/20
                    bg-white/10
                    text-white/80
                    backdrop-blur
                  "
                >
                  {t}
                </span>
              ))}
            </div>

            {/* Arrow bottom-right */}
            <span
              className="
                grid h-10 w-10 place-items-center
                rounded-full
                border border-white/15
                bg-black/20
                backdrop-blur
              "
              aria-hidden="true"
            >
              <svg
  width="18"
  height="18"
  viewBox="0 0 24 24"
  fill="none"
  stroke="#BEE362"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
>
  <path d="M7 7h10v10" />
  <path d="M7 17 17 7" />
</svg>

            </span>
          </div>
        </div>
      )}
    </a>
  );
}

